import { BookingLink } from "../components/BookingLink.tsx";
import { Reveal } from "../components/Reveal.tsx";
import { TreatmentGlyph } from "../components/TreatmentGlyph.tsx";
import { ClampedText } from "../components/ClampedText.tsx";
import { formatDuration, formatGbp } from "../lib/format.ts";
import { splitDescription } from "../lib/treatment.ts";
import { OfferBadge, Price } from "../components/Price.tsx";
import type { CatalogueResponse, Service } from "../lib/api.ts";

interface TreatmentsProps {
  catalogue: CatalogueResponse | null;
  error: string | null;
}

/**
 * The signature treatment, then the rest of the menu.
 *
 * The signature is whichever treatment the studio has ticked as one. It is
 * lifted out of its category rather than shown twice, and that category's
 * heading becomes "More facials" so the grid reads on from it. With no
 * signature ticked, every category is simply a grid.
 */
export function Treatments({ catalogue, error }: TreatmentsProps) {
  const signature =
    catalogue?.categories
      .flatMap((category) => category.services)
      .find((service) => service.signature) ?? null;

  // A category emptied by lifting out the signature, or one with nothing in
  // it yet, has no business leaving a heading on the page.
  const groups = (catalogue?.categories ?? [])
    .map((category) => ({
      category,
      services: category.services.filter((service) => service !== signature),
      continues: signature !== null && category.services.includes(signature),
    }))
    .filter((group) => group.services.length > 0);

  return (
    <>
      <div id="treatments" className="menu">
        {error && (
          <div className="page">
            <p className="notice notice--error menu__status">
              {error} You can still reach the studio on 07809 888733.
            </p>
          </div>
        )}

        {!catalogue && !error && (
          <div className="page menu__status" aria-live="polite">
            <span className="spinner" /> Loading the menu…
          </div>
        )}

        {signature && (
          <SignatureTreatment service={signature} />
        )}

        {groups.map(({ category, services, continues }) => (
          <section
            key={category.id}
            className="menu-group"
            aria-labelledby={`menu-group-${category.id}`}
          >
            <div className="page">
              <Reveal className="menu-group__head">
                <h2 id={`menu-group-${category.id}`} className="menu-group__title">
                  {continues ? `More ${category.name.toLowerCase()}` : category.name}
                </h2>
                {category.blurb && (
                  <p className="menu-group__blurb">{category.blurb}</p>
                )}
              </Reveal>

              <ul className="menu-grid">
                {services.map((service, index) => (
                  <Reveal
                    as="li"
                    key={service.id}
                    delay={(index % 3) * 90}
                    className="menu-card"
                  >
                    <TreatmentCard service={service} />
                  </Reveal>
                ))}
              </ul>
            </div>
          </section>
        ))}

        {catalogue && (
          <div className="page">
            <p className="menu__note">
              A {catalogue.depositPercent}% deposit secures your appointment;
              the balance is settled on the day. Cancel with more than{" "}
              {catalogue.cancellationWindowHours} hours notice and it is
              refunded in full, automatically.
            </p>
          </div>
        )}
      </div>
    </>
  );
}

/** The photograph, or the treatment's glyph on a quiet panel until there is one. */
function TreatmentPhoto({
  service,
  className,
}: {
  service: Service;
  className: string;
}) {
  return (
    <div className={`${className} placeholder`}>
      {service.imageUrl ? (
        <img
          src={service.imageUrl}
          alt={service.imageAltText ?? service.name}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <span className="treatment-photo__glyph">
          <TreatmentGlyph slug={service.slug} />
        </span>
      )}
    </div>
  );
}

function SignatureTreatment({
  service,
}: {
  service: Service;
}) {
  const { body } = splitDescription(service.description);

  return (
    <section className="signature" aria-labelledby="signature-name">
      <div className="signature__inner">
        <TreatmentPhoto service={service} className="signature__photo" />

        <Reveal className="signature__copy">
          <h2 id="signature-name" className="signature__name">
            {service.name}
          </h2>
          {body && (
            <ClampedText
              text={body}
              className="signature__description"
              label={service.name}
              lines={9}
            />
          )}

          <dl className="signature__facts">
            <div>
              <dt>Duration</dt>
              <dd>{formatDuration(service.durationMinutes)}</dd>
            </div>
            <div>
              <dt>Price</dt>
              <dd>
                <Price
                  className="price--now-first"
                  pricePence={service.pricePence}
                  originalPence={service.originalPence}
                />
              </dd>
            </div>
            <div>
              <dt>Deposit</dt>
              <dd>{formatGbp(service.depositPence)}</dd>
            </div>
          </dl>

          {/* Only the treatment's own offer is badged. A site-wide one is
              announced once, in the band under the photograph. */}
          {service.offer && !service.offer.siteWide && (
            <OfferBadge
              label={service.offer.label}
              descriptor={service.offer.descriptor}
            />
          )}

          <BookingLink
            className="btn btn--lg signature__book"
            aria-label={`Book ${service.name}`}
          >
            Book this treatment
          </BookingLink>
        </Reveal>
      </div>
    </section>
  );
}

function TreatmentCard({ service }: { service: Service }) {
  const { subtitle, body } = splitDescription(service.description);
  const duration = formatDuration(service.durationMinutes);

  return (
    <>
      <TreatmentPhoto service={service} className="menu-card__photo" />
      <p className="menu-card__kicker">
        {subtitle}
      </p>
      <h3 className="menu-card__name">{service.name}</h3>
      <div className="menu-card__details">
        {body && (
          <ClampedText
            text={body}
            className="menu-card__description"
            label={service.name}
            lines={4}
          />
        )}
      </div>
      <div className="menu-card__foot">
        <div className="menu-card__price">
          <span className="menu-card__duration">{duration}<span aria-hidden="true"> · </span></span>
          <Price
            className="price--now-first"
            pricePence={service.pricePence}
            originalPence={service.originalPence}
          />
          {service.offer && !service.offer.siteWide && (
            <OfferBadge
              label={service.offer.label}
              descriptor={service.offer.descriptor}
            />
          )}
        </div>
        <BookingLink
          className="menu-card__book"
          aria-label={`Book ${service.name}`}
        >
          Book
        </BookingLink>
      </div>
    </>
  );
}
